import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import Button from "./Button";
import { Field, TextArea, SelectField } from "./Field";
import NorthStar from "./NorthStar";
import client, { formatApiError } from "../lib/api";

const INITIAL = {
  name: "",
  company: "",
  work_email: "",
  campaign: "",
  talent_requested: "",
  budget_range: "",
  timeline: "",
  additional_details: "",
};

export default function BrandInquiry() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await client.post("/brand-inquiries", form);
      setSent(true);
      setForm(INITIAL);
      toast.success("Inquiry received. Our partnerships team will respond shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="brand-inquiry" className="border-t border-charcoal py-16 sm:py-32" data-testid="brand-inquiry-section">
      <div className="mx-auto max-w-[1600px] px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="label mb-8">Brand Inquiry</div>
            <h2 className="font-sans font-semibold uppercase text-warm text-4xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-[-0.02em]">
              Let's build something with impact.
            </h2>
            <p className="mt-8 text-silver text-lg leading-relaxed font-light max-w-md">
              Tell us about your brand and the outcome you're after. We'll bring the talent and
              the strategy to match.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          {sent ? (
            <Reveal>
              <div className="border border-charcoal p-10 sm:p-14" data-testid="brand-success">
                <NorthStar className="w-6 h-6 text-warm mb-6" />
                <h3 className="font-serif text-4xl text-warm">Inquiry received.</h3>
                <p className="mt-4 text-silver font-light max-w-md">
                  Thank you. Our partnerships team will review your inquiry and respond with next
                  steps.
                </p>
                <button
                  className="mt-8 label link-underline !text-warm"
                  onClick={() => setSent(false)}
                  data-testid="brand-reset"
                >
                  Submit another
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10" data-testid="brand-form">
                <Field label="Name" name="name" value={form.name} onChange={onChange} required testId="brand-name" />
                <Field label="Company" name="company" value={form.company} onChange={onChange} testId="brand-company" />
                <Field label="Work email" name="work_email" type="email" value={form.work_email} onChange={onChange} required testId="brand-email" />
                <Field label="Talent requested" name="talent_requested" value={form.talent_requested} onChange={onChange} testId="brand-talent" />
                <div className="sm:col-span-2">
                  <Field label="Campaign or project" name="campaign" value={form.campaign} onChange={onChange} testId="brand-campaign" />
                </div>
                <SelectField
                  label="Budget range"
                  name="budget_range"
                  value={form.budget_range}
                  onChange={onChange}
                  testId="brand-budget"
                  options={["Under $10k", "$10k – $25k", "$25k – $50k", "$50k – $100k", "$100k+"]}
                />
                <SelectField
                  label="Timeline"
                  name="timeline"
                  value={form.timeline}
                  onChange={onChange}
                  testId="brand-timeline"
                  options={["Immediate", "This quarter", "Next quarter", "Exploratory"]}
                />
                <div className="sm:col-span-2">
                  <TextArea label="Additional details" name="additional_details" value={form.additional_details} onChange={onChange} rows={4} testId="brand-details" />
                </div>
                <div className="sm:col-span-2 mt-2">
                  <Button type="submit" variant="primary" disabled={loading} data-testid="brand-submit">
                    {loading ? "Submitting…" : "Submit Inquiry"}
                  </Button>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
